const styleInjectedRoots = new WeakSet();

const AD_BADGE_CLASS = 'storeeel-ad-badge';
const AD_BADGE_TEXT_CLASS = 'storeeel-ad-badge__text';
const AD_BADGE_MOBILE_CLASS = 'storeeel-ad-badge--mobile';
const AD_DISCLOSURE_CLASS = 'storeeel-ad-disclosure';
const AD_DISCLOSURE_OPEN_CLASS = 'storeeel-ad-disclosure--open';
const AD_DISCLOSURE_TEXT_CLASS = 'storeeel-ad-disclosure__text';
const AD_DISCLOSURE_LINK_CLASS = 'storeeel-ad-disclosure__link';
const AD_DISCLOSURE_CLOSE_CLASS = 'storeeel-ad-disclosure__close';
const AD_POLICY_URL = 'https://www.storeeel.com/advertising-policy';

let badgeSequence = 0;
let outsideClickHandler = null;
const delegatedBadgeClickRoots = new WeakSet();

const ensureStyle = (root = document) => {
  const styleRoot = (typeof ShadowRoot !== 'undefined' && root instanceof ShadowRoot) ? root : document.head;
  if (!styleRoot || styleInjectedRoots.has(styleRoot)) return;
  const badgeStyle = document.createElement('style');
  badgeStyle.id = 'storeeel-ad-badge-style';
  badgeStyle.textContent = `
    .${AD_BADGE_CLASS} {
      position: absolute;
      top: 16px;
      left: 16px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0 12px;
      /* 箱の高さは固定する。上下paddingやフォント本来の行送りに任せると、埋め込み先の
         font-family（バッジは指定を持たず継承する）で高さが変わり、サイトごとにラベルの
         大きさが違って見える。14px * 1.5em（line-height: normal の最大）でも収まる値。 */
      height: 22px;
      border-radius: 999px;
      background: rgba(0, 0, 0, 0.40);
      z-index: 20;
      pointer-events: auto;
      cursor: pointer;
      user-select: none;
    }
    .${AD_BADGE_TEXT_CLASS} {
      color: #fff;
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      /* 行ボックスも font-size と同じ高さに固定する。normal だとフォント固有の行送りになり、
         上下非対称なぶんだけ文字が箱の中でずれる。1 にすれば上下の half-leading が均等に
         削られ、align-items: center と合わせて文字が箱の中央に来る。
         なお指定を消すのは不可。storeeelの枠は line-height: 0 を継承するため文字が潰れる。 */
      line-height: 1;
      letter-spacing: 0.08em;
    }
    .${AD_BADGE_CLASS}.${AD_BADGE_MOBILE_CLASS} {
      position: fixed;
      top: 48px;
      left: 4%;
      z-index: 999999;
      transform: none;
    }
    .${AD_DISCLOSURE_CLASS} {
      display: none;
      position: fixed;
      max-width: min(360px, calc(100vw - 24px));
      padding: 14px 44px 14px 16px;
      border-radius: 12px;
      background: rgba(0, 0, 0, 0.92);
      color: #fff;
      z-index: 1000000;
      box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35);
      backdrop-filter: blur(4px);
      line-height: 1.5;
      box-sizing: border-box;
    }
    .${AD_DISCLOSURE_CLASS}.${AD_DISCLOSURE_OPEN_CLASS} {
      display: block;
    }
    .${AD_DISCLOSURE_TEXT_CLASS} {
      margin: 0 0 12px;
      padding-right: 2px;
      white-space: pre-wrap;
      font-size: 13px;
      font-weight: 500;
      line-height: 1.6;
    }
    .${AD_DISCLOSURE_LINK_CLASS} {
      color: #7fc5ff;
      font-size: 13px;
      font-weight: 700;
      text-decoration: underline;
      text-underline-offset: 2px;
    }
    .${AD_DISCLOSURE_CLOSE_CLASS} {
      position: absolute;
      top: 8px;
      right: 8px;
      width: 24px;
      height: 24px;
      border: 0;
      background: transparent;
      color: #fff;
      font-size: 18px;
      line-height: 1;
      cursor: pointer;
      padding: 0;
    }
  `;
  styleRoot.appendChild(badgeStyle);
  styleInjectedRoots.add(styleRoot);
};

const closeDisclosure = (panel) => {
  if (!panel) return;
  panel.classList.remove(AD_DISCLOSURE_OPEN_CLASS);
  panel.dataset.anchorBadgeId = '';
  if (outsideClickHandler) {
    document.removeEventListener('click', outsideClickHandler, true);
    outsideClickHandler = null;
  }
};

const ensureDisclosurePanel = () => {
  const root = document.querySelector('.storeeel-modal') || document.body;
  ensureStyle(root.getRootNode?.() || document);
  let panel = root.querySelector(`.${AD_DISCLOSURE_CLASS}`);
  if (panel) return panel;

  panel = document.createElement('div');
  panel.className = AD_DISCLOSURE_CLASS;
  panel.innerHTML = `
    <button type="button" class="${AD_DISCLOSURE_CLOSE_CLASS}" aria-label="閉じる">×</button>
    <p class="${AD_DISCLOSURE_TEXT_CLASS}">本コンテンツは広告主の依頼に基づき、当メディアの承認を経て掲載しています。
本メディアのテーマおよび閲覧文脈に沿った内容として配信されています。</p>
    <a class="${AD_DISCLOSURE_LINK_CLASS}" href="${AD_POLICY_URL}" target="_blank" rel="noopener noreferrer">広告ポリシーを見る</a>
  `;
  root.appendChild(panel);

  const closeButton = panel.querySelector(`.${AD_DISCLOSURE_CLOSE_CLASS}`);
  if (closeButton) {
    closeButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      closeDisclosure(panel);
    });
  }

  return panel;
};

const positionDisclosure = (panel, badge) => {
  const rect = badge.getBoundingClientRect();
  const margin = 12;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const panelWidth = Math.min(360, Math.max(220, viewportWidth - margin * 2));
  panel.style.maxWidth = `${panelWidth}px`;

  let left = rect.left;
  if (left + panelWidth + margin > viewportWidth) {
    left = viewportWidth - panelWidth - margin;
  }
  if (left < margin) left = margin;

  let top = rect.bottom + 8;
  const estimatedHeight = panel.offsetHeight || 170;
  if (top + estimatedHeight + margin > viewportHeight) {
    top = Math.max(margin, rect.top - estimatedHeight - 8);
  }

  panel.style.left = `${Math.round(left)}px`;
  panel.style.top = `${Math.round(top)}px`;
};

const activateBadge = (badge, event) => {
  event.preventDefault();
  event.stopPropagation();

  const panel = ensureDisclosurePanel();
  const badgeId = badge.dataset.storeeelBadgeId;
  const isSameBadgeOpen = panel.classList.contains(AD_DISCLOSURE_OPEN_CLASS)
    && panel.dataset.anchorBadgeId === badgeId;

  if (isSameBadgeOpen) {
    closeDisclosure(panel);
    return;
  }

  panel.dataset.anchorBadgeId = badgeId;
  panel.classList.add(AD_DISCLOSURE_OPEN_CLASS);
  positionDisclosure(panel, badge);

  if (!outsideClickHandler) {
    outsideClickHandler = (outsideEvent) => {
      const target = outsideEvent.target;
      if (panel.contains(target)) return;
      if (target?.closest?.(`.${AD_BADGE_CLASS}`)) return;
      closeDisclosure(panel);
    };
    document.addEventListener('click', outsideClickHandler, true);
  }
};

const findBadgeAtPoint = (root, event) => {
  const badges = Array.from(root.querySelectorAll?.(`.${AD_BADGE_CLASS}`) || []);
  return badges.find((badge) => {
    const rect = badge.getBoundingClientRect();
    return event.clientX >= rect.left
      && event.clientX <= rect.right
      && event.clientY >= rect.top
      && event.clientY <= rect.bottom;
  });
};

const bindDelegatedBadgeClickInRoot = (root = document) => {
  if (typeof ShadowRoot === 'undefined' || !(root instanceof ShadowRoot)) return;
  const eventRoot = root;
  if (!eventRoot || delegatedBadgeClickRoots.has(eventRoot)) return;
  delegatedBadgeClickRoots.add(eventRoot);
  eventRoot.addEventListener('click', (event) => {
    const badge = event.composedPath?.()
      ?.find(node => node instanceof HTMLElement && node.classList?.contains(AD_BADGE_CLASS))
      || findBadgeAtPoint(eventRoot, event);
    if (!badge) return;
    event.__storeeelAdBadgeHandled = true;
    activateBadge(badge, event);
  }, true);
};

const bindBadgeInteraction = (badge) => {
  if (!badge || badge.dataset.storeeelBadgeBound === 'true') return;
  bindDelegatedBadgeClickInRoot(badge.getRootNode?.() || document);

  if (!badge.dataset.storeeelBadgeId) {
    badgeSequence += 1;
    badge.dataset.storeeelBadgeId = `storeeel-ad-badge-${badgeSequence}`;
  }

  const onActivate = (event) => {
    if (event.__storeeelAdBadgeHandled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    activateBadge(badge, event);
  };

  badge.addEventListener('click', onActivate);
  badge.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      onActivate(event);
    }
  });
  badge.dataset.storeeelBadgeBound = 'true';
};

const createBadge = () => {
  ensureStyle();
  const badge = document.createElement('div');
  badge.className = AD_BADGE_CLASS;
  badge.setAttribute('role', 'button');
  badge.setAttribute('tabindex', '0');
  badge.setAttribute('aria-label', 'PRについて');

  const badgeText = document.createElement('div');
  badgeText.className = AD_BADGE_TEXT_CLASS;
  badgeText.textContent = 'PR';

  badge.appendChild(badgeText);
  return badge;
};

const applyPlacement = (badge) => {
  const isMobile = Boolean(window?.storeeelItems?.isMobile);

  if (isMobile) {
    badge.classList.add(AD_BADGE_MOBILE_CLASS);
    badge.style.display = 'inline-flex';
  } else {
    badge.classList.remove(AD_BADGE_MOBILE_CLASS);
    badge.style.display = 'inline-flex';
  }
};

export const setAdBadge = (wrapper, shouldShow) => {
  if (!wrapper) return;

  let existing = wrapper.querySelector(`.${AD_BADGE_CLASS}`);

  if (shouldShow) {
    ensureStyle(wrapper.getRootNode?.() || document);
    if (getComputedStyle(wrapper).position === 'static') {
      wrapper.style.position = 'relative';
    }
    if (!existing) {
      existing = createBadge();
      wrapper.appendChild(existing);
    }
    applyPlacement(existing);
    // 広告バッジを押すと、掲載方針とポリシーリンクをポップオーバー表示する
    bindBadgeInteraction(existing);
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => {
        bindDelegatedBadgeClickInRoot(existing.getRootNode?.() || document);
      });
    } else {
      bindDelegatedBadgeClickInRoot(existing.getRootNode?.() || document);
    }
  } else if (existing) {
    const panel = document.querySelector(`.${AD_DISCLOSURE_CLASS}`);
    if (panel && panel.dataset.anchorBadgeId === existing.dataset.storeeelBadgeId) {
      closeDisclosure(panel);
    }
    existing.remove();
  }
};
